import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SkillSearch from './SkillSearch';
import SkillEditModal from './SkillEditModal';
import SkillAddModal from './SkillAddModal';
import SkillTableHeader from './SkillTableHeader';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../routes';

// --- Fuera del componente: funciones puras sin dependencias de estado ---
// No se recrean en cada render, eliminando presión sobre React.memo de los hijos.

const SKILL_TYPE_COLOR_MAP = {
    'Unique Skills': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Inherited Skills': 'bg-gray-100 text-gray-600 border-gray-300',
    'Green Skills': 'bg-green-100 text-green-800 border-green-300',
    'Blue Skills': 'bg-blue-100 text-blue-800 border-blue-300',
    'Red Skills': 'bg-red-100 text-red-800 border-red-300',
    'Orange Skills': 'bg-orange-100 text-orange-800 border-orange-300',
    'Purple Skills': 'bg-purple-100 text-purple-800 border-purple-300',
    'Gold skills': 'bg-amber-100 text-amber-800 border-amber-300',
    'Scenario Gold Skill': 'bg-amber-200 text-amber-900 border-amber-400',
    'Evolution Skills': 'bg-pink-100 text-pink-800 border-pink-300',
};

const RARITY_COLORS = { 1: 'border-gray-400', 2: 'border-green-500', 3: 'border-blue-500', 4: 'border-purple-500', 5: 'border-yellow-500' };
const RARITY_BG = { 1: 'bg-gray-100', 2: 'bg-green-50', 3: 'bg-blue-50', 4: 'bg-purple-50', 5: 'bg-yellow-50' };
const RARITY_STAR_COLOR = { 1: 'text-gray-400', 2: 'text-green-500', 3: 'text-blue-500', 4: 'text-purple-500', 5: 'text-yellow-500' };

// A1 — Franja izquierda por tipo (signature element)
const SKILL_TYPE_BORDER = {
    'Unique Skills': 'border-l-yellow-400',
    'Inherited Skills': 'border-l-gray-300 dark:border-l-gray-600',
    'Green Skills': 'border-l-green-500',
    'Blue Skills': 'border-l-blue-500',
    'Red Skills': 'border-l-red-500',
    'Orange Skills': 'border-l-orange-400',
    'Purple Skills': 'border-l-purple-500',
    'Gold skills': 'border-l-amber-400',
    'Scenario Gold Skill': 'border-l-amber-600',
    'Evolution Skills': 'border-l-pink-500',
};

// A2 — Ring de rareza en el ícono
const RARITY_RING = {
    1: 'ring-gray-300 dark:ring-gray-600',
    2: 'ring-green-400',
    3: 'ring-blue-400',
    4: 'ring-purple-400',
    5: 'ring-yellow-400 shadow-[0_0_8px_rgba(234,179,8,0.4)]',
};

const getSkillIcon = (iconId, rarity) => {
    const iconPath = `/images/Icon/Skill_Icons/icon_skill_${iconId}.png`;
    return (
        <div className="flex flex-col items-center gap-0.5">
            <div className={`w-14 h-14 rounded-lg overflow-hidden border-2 ring-2 ring-offset-1 ${RARITY_COLORS[rarity] || 'border-gray-400'} ${RARITY_BG[rarity] || 'bg-gray-100'} ${RARITY_RING[rarity] || 'ring-gray-300'} flex items-center justify-center`}>
                <img
                    src={iconPath}
                    alt={`Skill ${iconId}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<span class="text-sm font-bold text-gray-600">${rarity || '?'}</span>`;
                    }}
                />
            </div>
            {rarity && (
                <div className={`flex gap-px text-xs leading-none ${RARITY_STAR_COLOR[rarity] || 'text-gray-400'}`}>
                    {[1, 2, 3, 4, 5].map(s => (
                        <span key={s}>{s <= rarity ? '★' : '☆'}</span>
                    ))}
                </div>
            )}
        </div>
    );
};
// --- Fin zona fuera del componente ---

const SkillTable = ({ skills, setSkills, fetchSkills }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [costMin, setCostMin] = useState('');
    const [costMax, setCostMax] = useState('');
    const [scoreMin, setScoreMin] = useState('');
    const [scoreMax, setScoreMax] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedSkill, setSelectedSkill] = useState(null);

    // Obtener información de autenticación
    const { isAdmin } = useAuth();
    const isUserAdmin = isAdmin();

    // Filtrar skills — useMemo: solo recalcula cuando los filtros o los datos cambian.
    // Con 1936 skills, evita el filtro en cada re-render del componente.
    const filteredSkills = useMemo(() => skills.filter(skill => {
        // 1. Text Search
        const matchesSearch = !searchTerm || (() => {
            const search = searchTerm.toLowerCase();
            const skillName = (skill.Skill_name || skill.Skill_name_jp || '').toLowerCase();
            const description = (skill.description || '').toLowerCase();
            return skillName.includes(search) || description.includes(search);
        })();

        // 2. Color Filter
        const matchesColor = !selectedColor || skill.color === selectedColor;

        // 3. Cost Range
        const matchesCost = (() => {
            if (costMin === '' && costMax === '') return true;
            const val = skill.cost ?? 0;
            const min = costMin === '' ? -Infinity : parseFloat(costMin);
            const max = costMax === '' ? Infinity : parseFloat(costMax);
            return val >= min && val <= max;
        })();

        // 4. Score Range
        const matchesScore = (() => {
            if (scoreMin === '' && scoreMax === '') return true;
            const val = skill.Score_Value ?? 0;
            const min = scoreMin === '' ? -Infinity : parseFloat(scoreMin);
            const max = scoreMax === '' ? Infinity : parseFloat(scoreMax);
            return val >= min && val <= max;
        })();

        return matchesSearch && matchesColor && matchesCost && matchesScore;
    }), [skills, searchTerm, selectedColor, costMin, costMax, scoreMin, scoreMax]);

    // Handler para editar skill
    const handleEdit = (skill) => {
        setSelectedSkill(skill);
        setIsModalOpen(true);
    };

    // Handler para guardar cambios
    const handleSave = (updatedSkill) => {
        setSkills(prevSkills =>
            prevSkills.map(skill =>
                skill.Id === updatedSkill.Id ? updatedSkill : skill
            )
        );
    };

    const handleAddSkill = (newSkill) => {
        setSkills(prev => [...prev, newSkill]);
        setIsAddModalOpen(false);
    };

    // Handler para cerrar modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedSkill(null);
    };

    // A3 — Limpiar todos los filtros (para el botón en SkillSearch)
    const handleClearAll = () => {
        setSearchTerm('');
        setSelectedColor('');
        setCostMin('');
        setCostMax('');
        setScoreMin('');
        setScoreMax('');
    };

    return (
        <div className="min-h-screen bg-panel-gray dark:bg-gray-900 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header Premium (Purely visual text) */}
                <SkillTableHeader />

                {/* Table Actions & Admin Controls (Restored from old Header logic) */}
                <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <span className="text-gray-600 dark:text-gray-400 font-medium bg-gray-50 dark:bg-gray-700/50 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600">
                            {t('SkillTable.total_skills')} <span className="font-bold text-gray-900 dark:text-white ml-1">{skills.length}</span>
                        </span>
                        {isUserAdmin && (
                            <span className="px-3 py-1.5 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border border-red-200 dark:border-red-800 text-xs font-bold tracking-widest uppercase rounded-lg shadow-sm">
                                {t('SkillTable.admin_mode')}
                            </span>
                        )}
                    </div>
                    {isUserAdmin && (
                        <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="bg-primary-green text-white px-5 py-2.5 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 shadow-sm font-semibold"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Skill
                        </button>
                    )}
                </div>

                {/* Search Bar Component */}
                <div className="mb-6">
                    <SkillSearch
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        selectedColor={selectedColor}
                        onColorChange={setSelectedColor}
                        costMin={costMin}
                        onCostMinChange={setCostMin}
                        costMax={costMax}
                        onCostMaxChange={setCostMax}
                        scoreMin={scoreMin}
                        onScoreMinChange={setScoreMin}
                        scoreMax={scoreMax}
                        onScoreMaxChange={setScoreMax}
                        totalCount={skills.length}
                        filteredCount={filteredSkills.length}
                        onClearAll={handleClearAll}
                    />
                </div>

                {/* Skills Table */}
                <div className="bg-card-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-primary-green dark:bg-green-700 text-white">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold">{t('SkillTable.headers.icon')}</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold">{t('SkillTable.headers.name')}</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold">{t('SkillTable.headers.description')}</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold">{t('SkillTable.headers.cost')}</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold">{t('SkillTable.headers.score')}</th>
                                    {isUserAdmin && (
                                        <th className="px-6 py-4 text-center text-sm font-semibold">{t('SkillTable.headers.actions')}</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredSkills.length === 0 ? (
                                    <tr>
                                        <td colSpan={isUserAdmin ? "6" : "5"} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                            {t('SkillTable.empty_state')}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSkills.map((skill) => (
                                        <tr key={skill.Id}
                                            className="bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-750 cursor-pointer"
                                            onClick={() => navigate(`${ROUTES.SKILL_DETAIL}/${skill.Id_skill}`)}
                                        >
                                            {/* Icon — border-l aquí porque en <tr> con border-collapse:collapse no se renderiza */}
                                            <td className={`px-6 py-4 border-l-4 ${SKILL_TYPE_BORDER[skill.color] || 'border-l-transparent'}`}>
                                                {getSkillIcon(skill.icon_id, skill.rarity)}
                                            </td>

                                            {/* Skill Name */}
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="text-[15px] font-bold tracking-tight text-gray-900 dark:text-white">
                                                        {skill.Skill_name || skill.Skill_name_jp || 'N/A'}
                                                    </span>
                                                    {skill.Skill_name && skill.Skill_name_jp && (
                                                        <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                            {skill.Skill_name_jp}
                                                        </span>
                                                    )}
                                                    {skill.color && (
                                                        <span className={`mt-1.5 inline-flex items-center self-start gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${SKILL_TYPE_COLOR_MAP[skill.color] || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                                                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"></span>
                                                            {skill.color}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Description */}
                                            <td className="px-6 py-4">
                                                <p className="text-sm text-gray-700 dark:text-gray-300 max-w-md line-clamp-2">
                                                    {skill.description || t('SkillTable.no_description')}
                                                </p>
                                            </td>

                                            {/* Cost */}
                                            <td className="px-4 py-4 text-center">
                                                <div className="flex flex-col items-center gap-0.5">
                                                    <span className="text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500 font-medium leading-none">
                                                        Cost
                                                    </span>
                                                    <span className="text-base font-mono font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                                                        {skill.cost !== null ? skill.cost : '—'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Score Value */}
                                            <td className="px-4 py-4 text-center">
                                                <div className="flex flex-col items-center gap-0.5">
                                                    <span className="text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500 font-medium leading-none">
                                                        Score
                                                    </span>
                                                    <span className="text-base font-mono font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                                                        {skill.Score_Value !== null ? skill.Score_Value : '—'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Actions (Admin only) */}
                                            {isUserAdmin && (
                                                <td className="px-6 py-4 text-center">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleEdit(skill); }}
                                                        className="px-3 py-1.5 bg-secondary-blue text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center gap-1 mx-auto"
                                                        title={t('SkillTable.edit_tooltip')}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                        {t('SkillTable.edit_button')}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer Info */}
                <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
                    <p>{t('SkillTable.footer_count', { count: filteredSkills.length })}</p>
                </div>
            </div>

            {/* Edit Modal */}
            <SkillEditModal
                skill={selectedSkill}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
            />

            {/* Add Modal */}
            <SkillAddModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSave={handleAddSkill}
            />
        </div>
    );
};

export default SkillTable;
// Revisado: 2025-12-18 I18n Updated6
